import React, { useState, useEffect, useRef } from 'react';
import { QuestionDisplay } from './QuestionDisplay';
import { Button, Card, CardBody } from './BaseComponents';
import { Question, QuestionResult } from '../types';
import { ExponentialProgressBar } from './ExponentialProgressBar';
import { demoAnalytics } from '../utils/analytics';
import { isAnswerCorrect } from '../utils/answerMatching';

/**
 * DemoQuestionWidget Component
 *
 * Interactive demo widget for the landing page that allows visitors to
 * experience the platform quality without signing up.
 * Matches the real QuizPage component flow for authenticity.
 *
 * Features:
 * - Displays curated sample questions
 * - Fully functional question answering
 * - Simple Previous/Next navigation (matching real quiz)
 * - Results shown only after completion
 * - No authentication required
 *
 * Props:
 * - questions: Array of demo questions to display
 * - onComplete: Optional callback when user completes all questions
 */
interface DemoQuestionWidgetProps {
  questions: Question[];
  onComplete?: () => void;
}

export const DemoQuestionWidget: React.FC<DemoQuestionWidgetProps> = ({
  questions,
  onComplete
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<{ [key: number]: string }>({});
  const [isCompleted, setIsCompleted] = useState(false);

  // Track if demo has started (to avoid duplicate tracking)
  const hasTrackedStart = useRef(false);
  const hasTrackedCompletion = useRef(false);

  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = userAnswers[currentQuestionIndex] || '';
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  /**
   * Counts the answers that match their question's key, resolving option
   * letters and option text the same way the backend scores them.
   */
  const countCorrectAnswers = () => Object.keys(userAnswers).filter(
    key => {
      const question = questions[Number(key)];
      return isAnswerCorrect(userAnswers[Number(key)], question.answerKey, question.optionsJson);
    }
  ).length;

  /**
   * Track demo start on component mount
   */
  useEffect(() => {
    if (!hasTrackedStart.current) {
      demoAnalytics.trackDemoStarted();
      hasTrackedStart.current = true;
    }
  }, []);

  /**
   * Track demo abandonment when user leaves before completing
   * This runs on component unmount
   */
  useEffect(() => {
    return () => {
      // Only track abandonment if demo wasn't completed
      if (!hasTrackedCompletion.current && hasTrackedStart.current) {
        const answeredCount = Object.keys(userAnswers).filter(
          key => userAnswers[Number(key)] !== ''
        ).length;

        demoAnalytics.trackDemoAbandoned(answeredCount, questions.length);
      }
    };
  }, [userAnswers, questions.length]);

  /**
   * Handle answer change (store answer locally)
   */
  const handleAnswerChange = (answer: string | number | string[]) => {
    setUserAnswers({
      ...userAnswers,
      [currentQuestionIndex]: String(answer)
    });
  };

  /**
   * Move to the next question or finish quiz
   */
  const handleNextQuestion = () => {
    // Track question completion
    if (currentAnswer) {
      demoAnalytics.trackQuestionCompleted(currentQuestionIndex + 1, questions.length);
    }

    if (!isLastQuestion) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  /**
   * Complete the demo and show results
   */
  const handleFinishQuiz = () => {
    // Track the final question completion
    if (currentAnswer) {
      demoAnalytics.trackQuestionCompleted(currentQuestionIndex + 1, questions.length);
    }

    // Calculate score and track demo completion
    const correctCount = countCorrectAnswers();

    demoAnalytics.trackDemoCompleted(correctCount, questions.length);
    hasTrackedCompletion.current = true; // Prevent abandonment tracking

    setIsCompleted(true);
    if (onComplete) {
      onComplete();
    }
  };

  /**
   * Move to the previous question
   */
  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  /**
   * Reset the demo to start over
   */
  const handleReset = () => {
    setCurrentQuestionIndex(0);
    setUserAnswers({});
    setIsCompleted(false);
  };

  // Calculate completion stats
  const answeredCount = Object.keys(userAnswers).filter(
    key => userAnswers[Number(key)] !== ''
  ).length;
  const correctCount = countCorrectAnswers();
  const progressPercentage = (answeredCount / questions.length) * 100;

  // Results screen after completion
  if (isCompleted) {
    return (
      <div className="space-y-6">
        {/* Results summary */}
        <Card className="border border-neutral-200" noHover={true}>
          <CardBody>
            <div className="text-center py-8">
              <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <svg className="w-12 h-12 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-3">
                Demo Complete!
              </h3>
              <p className="text-xl text-gray-600 mb-6">
                You scored {correctCount} out of {questions.length} correct
              </p>
              <div className="inline-block mb-8">
                <div className="text-5xl font-bold text-blue-600">
                  {Math.round((correctCount / questions.length) * 100)}%
                </div>
                <p className="text-sm text-gray-500 mt-2">Final Score</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Review answers with worked solutions */}
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-gray-900">Review Your Answers</h3>
          {questions.map((question, index) => {
            const userAnswer = userAnswers[index] || '';
            const isCorrect = isAnswerCorrect(userAnswer, question.answerKey, question.optionsJson);

            return (
              <Card key={index} className="border border-neutral-200" noHover={true}>
                <CardBody>
                  <QuestionDisplay
                    questionResult={{
                      question: question,
                      userAnswer: userAnswer,
                      isCorrect: isCorrect,
                      questionOrder: index + 1
                    }}
                    userAnswer={userAnswer}
                    onAnswerSubmit={() => {}} // Read-only in review mode
                    shuffleSeed="demo"
                    showResult={true}
                  />
                </CardBody>
              </Card>
            );
          })}
        </div>

        {/* CTA buttons */}
        <div className="text-center py-6">
          <p className="text-lg text-gray-700 mb-6">
            This is just a taste of what ThinkSphere offers. Access thousands more questions
            with detailed explanations and track your progress in real-time.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => {
                // Track signup after demo - critical conversion metric
                demoAnalytics.trackSignupAfterDemo();

                // Find the Sign Up button in the header and click it
                const buttons = Array.from(document.querySelectorAll('button'));
                const signUpButton = buttons.find(button => button.textContent?.trim() === 'Sign Up');
                if (signUpButton) {
                  signUpButton.click();
                }
              }}
              className="px-8 py-3 min-h-[48px] text-base font-semibold"
            >
              Start Your Free Trial
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={handleReset}
              className="px-8 py-3 min-h-[48px] text-base"
            >
              Try Demo Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Create QuestionResult for current question
  const questionResult: QuestionResult = {
    question: currentQuestion,
    userAnswer: currentAnswer,
    isCorrect: false, // Don't reveal correctness until completion
    questionOrder: currentQuestionIndex + 1
  };

  // Quiz in progress
  return (
    <div className="space-y-6">
      {/* Progress Bar Section (matching real quiz) */}
      <Card className="border border-neutral-200" noHover={true}>
        <CardBody>
          <div className="flex justify-between items-center mb-4">
            <span className="font-medium text-neutral-900">Demo Progress</span>
            <span className="text-sm text-neutral-600">
              Question {currentQuestionIndex + 1} of {questions.length}
            </span>
          </div>
          <ExponentialProgressBar progress={progressPercentage / 100} />
        </CardBody>
      </Card>

      {/* Question Display Section (matching real quiz) */}
      <Card className="border border-neutral-200" noHover={true}>
        <CardBody>
          <QuestionDisplay
            questionResult={questionResult}
            userAnswer={currentAnswer}
            onAnswerSubmit={handleAnswerChange}
            onMoveToNext={handleNextQuestion}
            shuffleSeed="demo"
          />
        </CardBody>
      </Card>

      {/* Navigation Buttons (matching real quiz) */}
      <div className="flex justify-between gap-3">
        <Button
          onClick={handlePreviousQuestion}
          variant="secondary"
          disabled={currentQuestionIndex === 0}
          className="min-h-[48px] px-6 py-3 text-base"
        >
          Previous
        </Button>

        {isLastQuestion ? (
          <Button
            onClick={handleFinishQuiz}
            disabled={!currentAnswer}
            variant="primary"
            className="bg-success-500 hover:bg-success-600 min-h-[48px] px-6 py-3 text-base"
          >
            Finish Demo
          </Button>
        ) : (
          <Button
            onClick={handleNextQuestion}
            disabled={!currentAnswer}
            variant="primary"
            className="min-h-[48px] px-6 py-3 text-base"
          >
            Next
          </Button>
        )}
      </div>
    </div>
  );
};

export default DemoQuestionWidget;
